import { useEffect, useRef, useState } from 'react';

/** Floor on reveal speed, so a trickling stream still visibly moves. */
export const MinCharsPerMs = 0.04; // 40 chars/s ≈ 7 words/s
/** Budget accrues at `backlog / TargetLagMs` chars per ms — i.e. a ~750ms
 *  buffer. Chunks arrive in clumps every few hundred ms; a buffer that deep
 *  almost never runs dry, so the flow stays continuous and eases off between
 *  clumps instead of stopping dead. Time-based, not frame-based: a 120Hz
 *  display must not reveal twice as fast with half the buffer. */
export const TargetLagMs = 750;
/** A single token longer than this (a URL, a long number) is released once
 *  this much budget has accrued rather than waiting for its full length. */
export const MaxWordWait = 24;

/** Paces a streaming message so it reveals at a steady cadence instead of in
 *  the clumps chunks actually arrive in. Incoming text accumulates as backlog;
 *  a requestAnimationFrame loop accrues a budget over elapsed time proportional
 *  to the backlog (see TargetLagMs) and releases the next whole word once the
 *  budget covers it — never mid-word, and never in a burst. Because the
 *  reveal is frame-driven, chunks landing within one frame coalesce into a
 *  single render.
 *
 *  Not streaming (history, completed replies) returns `content` as-is, and a
 *  stream ending flushes immediately. Content that stops being an extension of
 *  what's shown (a reconnect re-delivery) snaps to the new text rather than
 *  animating backwards. */
export function useSmoothStream(content: string, isStreaming: boolean): string {
  const [revealed, setRevealed] = useState(content);
  const revealedRef = useRef(content);
  const budgetRef = useRef(0);
  const lastTickRef = useRef<number | null>(null);

  useEffect(() => {
    const snapTo = (text: string) => {
      revealedRef.current = text;
      setRevealed(text);
    };
    if (!isStreaming || !content.startsWith(revealedRef.current)) {
      budgetRef.current = 0;
      lastTickRef.current = null;
      snapTo(content);
      return;
    }
    if (revealedRef.current.length === content.length) {
      return;
    }

    let frame = 0;
    const step = () => {
      const now = performance.now();
      const elapsedMs = lastTickRef.current === null ? 0 : now - lastTickRef.current;
      lastTickRef.current = now;
      const revealedLength = revealedRef.current.length;
      const backlog = content.length - revealedLength;
      budgetRef.current += elapsedMs * Math.max(MinCharsPerMs, backlog / TargetLagMs);
      let nextLength = revealedLength;
      // Release whole words while the budget covers them (a long token is
      // released at MaxWordWait so it can't stall the stream).
      while (nextLength < content.length) {
        let wordEnd = nextLength;
        while (wordEnd < content.length && /\s/.test(content[wordEnd])) {
          wordEnd++;
        }
        while (wordEnd < content.length && !/\s/.test(content[wordEnd])) {
          wordEnd++;
        }
        const cost = Math.min(wordEnd - nextLength, MaxWordWait);
        if (budgetRef.current < cost) {
          break;
        }
        budgetRef.current -= cost;
        nextLength = wordEnd;
      }
      if (nextLength > revealedLength) {
        snapTo(content.slice(0, nextLength));
      }
      if (nextLength < content.length) {
        frame = requestAnimationFrame(step);
      } else {
        budgetRef.current = 0;
        lastTickRef.current = null;
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [content, isStreaming]);

  return isStreaming ? revealed : content;
}
